import { Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField, Autocomplete, Box, Typography } from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import { Member } from '../../Models/Member/Member.model';
import { ITeamRoleAlternate } from '../../Models/CareTeams/TeamRoleAlternate.model';
import IProviderSelectOption from '../../Models/Provider/ProviderSelectOption.model';
import IRole from '../../Models/Role/Role.model';
import { ICareTeam } from '../../Models/CareTeams/CareTeam.model';
import { useAppDispatch } from '../../store/store';
import { updateTeam } from '../../store/careTeams.slice';

interface SelectAlternateProviderDialogProps {
    open: boolean;
    onClose: () => void;
    member: Member;
    roles: IRole[];
    providerOptions: IProviderSelectOption[];
    selectedTeam: ICareTeam;
    onSave?: (updatedAlternates: ITeamRoleAlternate[]) => void;
    onRefresh?: () => void;
}

const SelectAlternateProviderDialog: React.FC<SelectAlternateProviderDialogProps> = ({
    open,
    onClose,
    member,
    roles,
    providerOptions,
    selectedTeam,
    onSave,
    onRefresh,
}) => {
    const [selectedProviders, setSelectedProviders] = useState<{ [roleId: string]: string | null }>({});
    const selectedProvidersRef = useRef<{ [roleId: string]: string | null }>({});
    const dispatch = useAppDispatch();
    const initialStateRef = useRef<{ team: ICareTeam; member: Member } | null>(null);

    useEffect(() => {
        if (!open) {
            initialStateRef.current = null;
            selectedProvidersRef.current = {};
            return;
        }
        if (!initialStateRef.current || initialStateRef.current.member.Id !== member.Id) {
            initialStateRef.current = { team: selectedTeam, member: member };

            const initialSelectedProviders: { [roleId: string]: string | null } = {};
            selectedTeam.TeamRoles.forEach((teamRole) => {
                const alternate = selectedTeam.TeamRoleAlternates?.find((alt) => alt.MemberId === member.Id && alt.RoleId === teamRole.RoleId);
                // An alternate whose provider is no longer active shows as blank, so start it blank too; otherwise Save keeps it.
                const isActiveProvider = providerOptions.some((p) => p.ProviderId === alternate?.AssignedProviderId);
                initialSelectedProviders[teamRole.RoleId] = isActiveProvider ? alternate?.AssignedProviderId ?? null : null;
            });
            selectedProvidersRef.current = { ...initialSelectedProviders };
            setSelectedProviders(initialSelectedProviders);
        }
    }, [open, member.Id, selectedTeam]);

    const handleProviderChange = (roleId: string, provider: IProviderSelectOption | null) => {
        const newProviderId = provider?.ProviderId || null;
        selectedProvidersRef.current[roleId] = newProviderId;
        setSelectedProviders((prev) => {
            const updated = { ...prev, [roleId]: newProviderId };
            return updated;
        });
    };

    const handleSave = async () => {
        if (!initialStateRef.current) return;

        const { team, member: currentMember } = initialStateRef.current;

        const newAlternatesForMember = Object.entries(selectedProvidersRef.current)
            .filter(([, providerId]) => providerId)
            .map(([roleId, providerId]) => {
                const provider = providerOptions.find((p) => p.ProviderId === providerId);
                return {
                    MemberId: currentMember.Id,
                    MemberFirstName: currentMember.FirstName,
                    MemberLastName: currentMember.LastName,
                    RoleId: roleId,
                    AssignedProviderId: providerId!,
                    ProviderName: provider?.ProviderName || '',
                };
            });

        const updatedAlternates: ITeamRoleAlternate[] = [
            ...(team.TeamRoleAlternates?.filter((alt) => alt.MemberId !== currentMember.Id) || []),
            ...newAlternatesForMember,
        ];

        if (onSave) {
            onSave(updatedAlternates);
            onClose();
        } else {
            const updatedTeam = {
                ...team,
                TeamRoleAlternates: updatedAlternates,
            };
            // Wait for the save to finish so the refresh doesn't pull the old alternates back into state
            await dispatch(updateTeam(updatedTeam));
            if (onRefresh) {
                onRefresh();
            } else {
                onClose();
            }
        }
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Select Alternate Providers</DialogTitle>
            <DialogContent>
                {initialStateRef.current?.team.TeamRoles.map((teamRole) => {
                    const role = roles.find((r) => r.Id === teamRole.RoleId);
                    const roleTitle = role?.Name || `Role (${teamRole.RoleId})`;
                    const availableProviders = providerOptions.filter(
                        (provider) => provider.RoleId === teamRole.RoleId && provider.ProviderId !== teamRole.AssignedProviderId
                    );
                    const selectedProviderId = selectedProviders[teamRole.RoleId];
                    const selectedProvider = selectedProviderId ? providerOptions.find((p) => p.ProviderId === selectedProviderId) : null;
                    return (
                        <Box key={teamRole.RoleId} sx={{ marginBottom: 2 }}>
                            <Typography variant="subtitle1">{roleTitle}</Typography>
                            {availableProviders.length === 0 ? (
                                <Typography variant="body2" color="textSecondary">
                                    No alternate providers available for this role
                                </Typography>
                            ) : (
                                <Autocomplete
                                    options={availableProviders}
                                    getOptionLabel={(option) => option.ProviderName}
                                    value={selectedProvider || null}
                                    isOptionEqualToValue={(option, value) => option?.ProviderId === value?.ProviderId}
                                    onChange={(event, newValue) => handleProviderChange(teamRole.RoleId, newValue)}
                                    renderInput={(params) => <TextField {...params} label="Provider" margin="normal" />}
                                />
                            )}
                        </Box>
                    );
                })}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
                <Button onClick={handleSave} color="primary">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SelectAlternateProviderDialog;
